import type { SignUpProfileFormData, AnamneseFormData, ParQFormData } from '../types/registration';
import { validateAnamneseContentFields } from './anamneseValidation';
import { getPhoneDigitLengthRange } from './masks';

export function validateName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'O nome é obrigatório';
  if (trimmed.length < 3) return 'O nome deve ter pelo menos 3 caracteres';
  if (trimmed.length > 100) return 'O nome deve ter no máximo 100 caracteres';
  if (trimmed.split(/\s+/).filter(Boolean).length < 2) return 'Digite o nome completo';
  return '';
}

export function validateEmail(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) return 'O email é obrigatório';
  if (trimmed.length < 5) return 'O email deve ter pelo menos 5 caracteres';
  if (trimmed.length > 255) return 'O email deve ter no máximo 255 caracteres';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'Digite um email válido';
  const [localPart, domain] = trimmed.split('@');
  if (
    localPart.startsWith('.') ||
    localPart.endsWith('.') ||
    domain.startsWith('.') ||
    domain.endsWith('.')
  ) {
    return 'Email inválido';
  }
  return '';
}

export const STUDENT_NOTE_MIN_LENGTH = 5;

export function validateStudentNote(
  note: string,
  options: { required?: boolean } = {},
): string {
  const { required = true } = options;
  const trimmed = note.trim();

  if (!trimmed) {
    return required ? 'Informe uma observação para registrar.' : '';
  }

  if (trimmed.length < STUDENT_NOTE_MIN_LENGTH) {
    return `A observação deve ter pelo menos ${STUDENT_NOTE_MIN_LENGTH} caracteres.`;
  }

  return '';
}

export function validatePassword(password: string): string {
  if (!password) return 'A senha é obrigatória';
  if (password.length < 6) return 'A senha deve ter pelo menos 6 caracteres';
  if (password.length > 100) return 'A senha deve ter no máximo 100 caracteres';
  return '';
}

export function validateConfirmPassword(password: string, confirm: string): string {
  if (!confirm) return 'A confirmação de senha é obrigatória';
  if (password !== confirm) return 'As senhas não coincidem';
  return '';
}

export function validatePhone(
  phone: string,
  callingCode = '55',
  options?: { requireMobile?: boolean },
): string {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return 'O telefone é obrigatório';

  const code = callingCode.split('_')[0] || '55';
  const range = getPhoneDigitLengthRange(code);
  const min = options?.requireMobile && code === '55' ? 11 : range.min;
  const max = options?.requireMobile && code === '55' ? 11 : range.max;
  if (digits.length < min || digits.length > max) {
    return code === '55'
      ? 'Digite um telefone válido com DDD'
      : 'Digite um telefone válido';
  }

  return '';
}

function isValidCpfDigits(digits: string): boolean {
  if (digits.length !== 11 || /^(\d)\1+$/.test(digits)) {
    return false;
  }

  let sum = 0;
  for (let index = 0; index < 9; index += 1) {
    sum += Number.parseInt(digits[index], 10) * (10 - index);
  }

  let remainder = (sum * 10) % 11;
  if (remainder === 10) {
    remainder = 0;
  }

  if (remainder !== Number.parseInt(digits[9], 10)) {
    return false;
  }

  sum = 0;
  for (let index = 0; index < 10; index += 1) {
    sum += Number.parseInt(digits[index], 10) * (11 - index);
  }

  remainder = (sum * 10) % 11;
  if (remainder === 10) {
    remainder = 0;
  }

  return remainder === Number.parseInt(digits[10], 10);
}

export function validateCpf(cpf: string, options: { required?: boolean } = {}): string {
  const { required = true } = options;
  const digits = cpf.replace(/\D/g, '');

  if (!digits) {
    return required ? 'O CPF é obrigatório' : '';
  }

  if (digits.length !== 11) {
    return 'Digite um CPF válido com 11 números';
  }

  if (!isValidCpfDigits(digits)) {
    return 'Digite um CPF válido';
  }

  return '';
}

export function isBrazilCountry(country: string | undefined | null): boolean {
  const normalized = (country ?? '').trim().toUpperCase();
  return !normalized || normalized === 'BR';
}

/** CPF obrigatório no Brasil, exceto para aluna marcada como estrangeira. */
export function requiresBrazilianCpf(
  country: string | undefined | null,
  isForeignStudent = false,
): boolean {
  if (isForeignStudent) {
    return false;
  }

  return isBrazilCountry(country);
}

export function validateSignUpProfile(
  data: SignUpProfileFormData,
  editMode = false,
  isForeignStudent = false,
): Record<string, string> {
  const errors: Record<string, string> = {};

  const nameErr = validateName(data.name);
  if (nameErr) errors.name = nameErr;

  const cpfRequired = requiresBrazilianCpf(data.country, isForeignStudent);
  const cpfError = validateCpf(data.cpf, { required: cpfRequired });
  if (cpfError) {
    errors.cpf = cpfError;
  }

  const phoneErr = validatePhone(data.phone, data.callingCode);
  if (phoneErr) errors.phone = phoneErr;

  return errors;
}

export function validateAnamnese(data: AnamneseFormData): Record<string, string> {
  return validateAnamneseContentFields(data);
}

export function validateParQ(_data: ParQFormData): Record<string, string> {
  return {};
}
